/**
 * Interface for reservation data structure
 */
export interface ReserveData {
  id?: string;
  status?: string;
  reservation_date?: string;
  [key: string]: unknown; // Para campos adicionales no especificados
}

/**
 * Interface for line items in a reservation
 */
export interface ReservationItem {
  description: string;
  quantity: number;
  price: number;
}

/**
 * Interface for installments configuration
 */
export interface InstallmentsConfig {
  installments_active: boolean;
  minimum_amount_terms: string;
  msi_payment_terms: string;
  allow_deferred_payments: boolean;
  minimum_amount_deferred: string;
  deferred_payment_terms: string;
}

/**
 * Interface for the decoded Link Parameters according to the official documentation
 */
export interface LinkParams {
  // Parámetros documentados según la documentación oficial
  referenceId?: string;           // Identificador de referencia de la reserva
  booking_number?: string;        // Número de reserva
  checkin?: string;               // Fecha de entrada (formato: YYYY-MM-DD)
  checkout?: string;              // Fecha de salida (formato: YYYY-MM-DD)
  guests?: number;                // Número de huéspedes
  rooms?: number;                 // Número de habitaciones
  total?: number;                 // Monto total de la reserva
  subtotal?: number;              // Subtotal de la reserva
  taxes?: number;                 // Impuestos aplicables
  discounts?: number;             // Descuentos aplicados
  currency?: string;              // Moneda de la transacción (default: "MXN")
  language?: string;              // Idioma (default: "es")
  url_success?: string;           // URL de redirección en caso de éxito
  url_failure?: string;           // URL de redirección en caso de fallo
  
  // Información del cliente
  first_name?: string;            // Nombre del cliente
  last_name?: string;             // Apellido del cliente
  email?: string;                 // Correo electrónico del cliente
  phone?: string;                 // Teléfono del cliente
  
  // Información del hotel
  hotel_slug?: string;            // Identificador único del hotel
  
  // Estructuras de datos adicionales que podrían pasarse
  reserve_data?: ReserveData;     // Datos de la reserva
  items?: ReservationItem[];      // Ítems de la reserva
  
  // Configuración de installments
  installments_config?: InstallmentsConfig; // Configuración de plazos
  
  // Configuración adicional
  disable_inputs?: boolean;       // Deshabilitar campos en el formulario
  disable_invoice?: boolean;      // Deshabilitar solicitud de factura
  concept?: string;               // Concepto adicional para categorización de la reserva
  source_currency?: string;       // Moneda origen para conversión
  fx_rate?: number;              // Tipo de cambio para conversión
  source_amount?: number;        // Monto en moneda origen
}

import logger from './logger.ts';

/**
 * Decodes URL parameters from a payment link
 *
 * @param {string} url - The payment link URL to decode
 * @returns {LinkParams | null} The decoded parameters or null if decoding fails
 */
export function decodeLinkParams(url: string): LinkParams | null {
  try {
    // Parse the URL
    const parsedUrl = new URL(url);

    // Get the encoded params
    const params = parsedUrl.searchParams.get('params');

    if (!params) {
      throw new Error('No params parameter found in URL');
    }

    // URL decode
    const decodedParams = decodeURIComponent(params);

    // Base64 decode
    const jsonString = atob(decodedParams);

    // Parse JSON
    const parsedParams = JSON.parse(jsonString) as LinkParams;
    
    return parsedParams;
  } catch (error) {
    logger.error('Error decoding link params:', error);
    return null;
  }
}
